// public/js/features/subscription/subscription-page.js
// "Subscription plan" page (User dropdown → superadmin only). An interactive
// price configurator + a classic tier comparison, driven by pricing-data.js.
//
//   • Top: pick quantities per device/service → live monthly total, the volume
//     tier it lands in (by TOTAL device count), and a cost breakdown.
//   • Middle: the five volume tiers as styled comparison columns (active tier
//     highlighted from the configurator).
//   • Bottom: the full per-tier price matrix + footnotes.
import { escapeHtml } from '../../utils/escape.js';
import { isAdminLevel } from '../../modules/rbac.js';
import { PRICING, ALL_PRODUCTS, tierIndexForCount, fmtUSD } from './pricing-data.js';

// deviceKey → quantity. Start with a small, meaningful example.
const qty = Object.fromEntries(ALL_PRODUCTS.map(p => [p.key, 0]));
qty.tracepro = 10;

const priceAt = (p, ti) => p.prices[ti];

// Total number of devices/tags/sensors (drives the volume tier). Checkbox
// add-ons (device:false) never count here.
function deviceCountNow() {
  return ALL_PRODUCTS.reduce((n, p) => n + (p.device ? (qty[p.key] || 0) : 0), 0);
}
// Effective billable quantity. A checkbox add-on, when enabled, multiplies by
// another product's count (`multiplyBy: 'tracepro'`) or by the total device
// count (`multiplyBy: 'deviceCount'`). Everything else uses its own quantity.
function effQty(p) {
  if (p.checkbox) {
    if (!qty[p.key]) return 0;
    return p.multiplyBy === 'deviceCount' ? deviceCountNow() : (qty[p.multiplyBy] || 0);
  }
  return qty[p.key] || 0;
}

function compute() {
  const deviceCount = deviceCountNow();
  const ti = tierIndexForCount(deviceCount);
  const lines = [];
  let monthly = 0;
  let onRequest = 0;
  for (const p of ALL_PRODUCTS) {
    const q = effQty(p);
    if (!q) continue;
    const unit = priceAt(p, ti);
    if (unit == null) { onRequest++; lines.push({ p, q, unit: null, sub: null }); continue; }
    const sub = unit * q;
    monthly += sub;
    lines.push({ p, q, unit, sub });
  }
  return { deviceCount, ti, lines, monthly, onRequest };
}

// Monthly total of the CURRENT configuration priced at a given tier (on-request
// items excluded) — powers the per-tier "your fleet" totals used for comparison.
function totalAtTier(ti) {
  let sum = 0;
  for (const p of ALL_PRODUCTS) {
    const q = effQty(p);
    if (!q) continue;
    const u = priceAt(p, ti);
    if (u != null) sum += q * u;
  }
  return sum;
}

// ── summary panel (re-rendered on every change; inputs are the source of truth) ─
function summaryHTML() {
  const s = compute();
  const tier = PRICING.tiers[s.ti];
  const rows = s.lines.length ? s.lines.map(l => `
    <div class="d-flex justify-content-between align-items-baseline py-1 border-bottom border-light-subtle">
      <div class="pe-2">
        <div class="small fw-semibold">${escapeHtml(l.p.name)}</div>
        <div class="text-muted" style="font-size:.72rem;">
          ${l.unit == null ? 'priced on request'
            : `${l.q} × ${fmtUSD(l.unit)}/mo${l.p.checkbox ? (l.p.multiplyBy === 'deviceCount' ? ' · per device' : ' · per TracePro') : ''}`}
        </div>
      </div>
      <div class="text-end fw-semibold small">${l.sub == null ? '—' : fmtUSD(l.sub)}</div>
    </div>`).join('') : '<div class="text-muted small py-2">Add some devices above to see the cost.</div>';

  return `
    <div class="d-flex align-items-center justify-content-between mb-1">
      <span class="text-muted small text-uppercase" style="letter-spacing:.04em;">Estimated cost</span>
      <span class="badge badge-soft-blue">Tier ${escapeHtml(tier.label)}</span>
    </div>
    <div class="pricing-total">${fmtUSD(s.monthly)}<span class="pricing-total-unit">/month</span></div>
    <div class="text-muted small mb-2">
      ${s.deviceCount} device${s.deviceCount === 1 ? '' : 's'} configured →
      <b>${escapeHtml(tier.label)}</b> volume tier${s.onRequest ? ` · ${s.onRequest} item${s.onRequest === 1 ? '' : 's'} on request` : ''}
    </div>
    <div class="pricing-breakdown mb-2">${rows}</div>
    <div class="text-muted" style="font-size:.72rem;">Recurring, per month. Excludes VAT &amp; shipping and the one-time consulting / installation / training fee (see below). Min. 3-year term.</div>
  `;
}

// A quantity stepper for one product, with end-customer detail bullets. Up to 3
// bullets render inline; more than 3 collapse behind a native "Details" toggle.
function detailBlock(p) {
  const d = p.details || [];
  if (!d.length) return '';
  // A detail item is either a plain string (escaped) or { html } for rich content (e.g. a link).
  const items = d.map(x => `<li>${typeof x === 'string' ? escapeHtml(x) : (x.html || '')}</li>`).join('');
  if (d.length > 3) {
    return `<details class="pricing-details"><summary>Details</summary><ul class="pricing-detail-list">${items}</ul></details>`;
  }
  return `<ul class="pricing-detail-list">${items}</ul>`;
}

function stepper(p) {
  const onReq = p.prices.every(v => v == null);
  const shortBadge = p.short ? `<span class="badge badge-soft-blue">${escapeHtml(p.short)}</span>` : '';
  const control = p.checkbox
    ? `<div class="form-check form-switch flex-shrink-0 pt-1 m-0 ps-0 d-flex align-items-center" style="min-height:auto;">
         <input class="form-check-input pricing-check m-0" type="checkbox" role="switch" data-product="${p.key}" ${qty[p.key] ? 'checked' : ''} aria-label="Enable ${escapeHtml(p.name)}">
       </div>`
    : `<div class="pricing-qty btn-group btn-group-sm flex-shrink-0" role="group" aria-label="${escapeHtml(p.name)} quantity">
         <button type="button" class="btn btn-outline-secondary pricing-step" data-act="dec" ${onReq ? 'disabled' : ''}>−</button>
         <input type="number" min="0" step="1" class="form-control form-control-sm text-center pricing-input"
                value="${qty[p.key] || 0}" ${onReq ? 'disabled' : ''} style="width:64px;" aria-label="${escapeHtml(p.name)} quantity">
         <button type="button" class="btn btn-outline-secondary pricing-step" data-act="inc" ${onReq ? 'disabled' : ''}>+</button>
       </div>`;
  return `
    <div class="pricing-row d-flex align-items-start gap-2 py-2" data-product="${p.key}">
      <div class="flex-grow-1 min-w-0">
        <div class="fw-semibold small d-flex align-items-center gap-1 flex-wrap">
          ${escapeHtml(p.name)}
          ${shortBadge}
          ${p.flagship ? '<span class="badge badge-soft-amber">Flagship</span>' : ''}
          ${p.needsTracker ? '<i class="fa-solid fa-link text-muted" title="Requires an active tracker or gateway on the asset" style="font-size:.7rem;"></i>' : ''}
        </div>
        <div class="text-muted" style="font-size:.72rem;">${escapeHtml(p.desc)}${onReq ? ' · <i>on request</i>' : ''}</div>
        ${detailBlock(p)}
      </div>
      ${control}
    </div>`;
}

function configuratorHTML() {
  const groups = PRICING.categories.map(c => `
    <div class="mb-3">
      <div class="text-muted text-uppercase fw-semibold" style="font-size:.68rem;letter-spacing:.05em;">${escapeHtml(c.title)}</div>
      ${c.note ? `<div class="text-muted mb-1" style="font-size:.7rem;">${escapeHtml(c.note)}</div>` : '<div class="mb-1"></div>'}
      ${c.products.map(stepper).join('')}
    </div>`).join('');

  return `
    <div class="card shadow-sm pricing-card mb-4">
      <div class="card-body">
        <div class="row g-4">
          <div class="col-lg-7">
            <h2 class="h6 mb-1">Configure your fleet</h2>
            <p class="text-muted small mb-3">Set how many of each device and service you need. The volume tier is picked automatically from your total device count.</p>
            ${groups}
          </div>
          <div class="col-lg-5">
            <div class="pricing-summary" id="pricing-summary">${summaryHTML()}</div>
          </div>
        </div>
      </div>
    </div>`;
}

// The five volume tiers as comparison columns (the "classic" cards), each with
// a computed "your fleet at this tier" monthly total and benefit bullets.
function tierCardsHTML(activeTi) {
  const flagship = ALL_PRODUCTS.find(p => p.flagship);
  const base = flagship ? priceAt(flagship, 0) : null;
  const cards = PRICING.tiers.map((t, i) => {
    const from = flagship ? priceAt(flagship, i) : null;
    const savePct = (base && from != null && i > 0) ? Math.round((1 - from / base) * 100) : 0;
    const feats = [
      i === 0 ? 'List price' : `Save ~${savePct}% vs list`,
      'All device & sensor types',
      'Full platform included',
    ];
    return `
      <div class="col">
        <div class="pricing-tier-card h-100 ${i === activeTi ? 'active' : ''}" data-tier="${i}">
          ${i === activeTi ? '<div class="pricing-tier-flag">Your tier</div>' : ''}
          <div class="pricing-tier-name">${escapeHtml(t.label)}</div>
          <div class="text-muted small mb-2">${escapeHtml(t.blurb)}</div>
          <div class="pricing-tier-from">
            <span class="text-muted" style="font-size:.7rem;">from</span>
            <span class="pricing-tier-price">${from == null ? '—' : fmtUSD(from)}</span>
            <span class="text-muted" style="font-size:.7rem;">/device·mo</span>
          </div>
          <div class="pricing-tier-yourfleet">
            <span class="text-muted" style="font-size:.66rem;text-transform:uppercase;letter-spacing:.03em;">Your fleet at this tier</span>
            <div class="pricing-tier-total" data-tier="${i}"><span class="v">${fmtUSD(totalAtTier(i))}</span><span class="u">/mo</span></div>
          </div>
          <ul class="pricing-tier-feats">
            ${feats.map(f => `<li>${escapeHtml(f)}</li>`).join('')}
          </ul>
        </div>
      </div>`;
  }).join('');
  return `
    <h2 class="h6 mb-2">Volume tiers</h2>
    <div class="row row-cols-2 row-cols-md-3 row-cols-xl-5 g-3 mb-3" id="pricing-tier-cards">${cards}</div>`;
}

// "Included in every plan" — the real platform capabilities, independent of tier.
function includedHTML() {
  return `
    <div class="card shadow-sm pricing-card mb-4">
      <div class="card-body">
        <h2 class="h6 mb-2">Included in every plan</h2>
        <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-2">
          ${PRICING.included.map(f => `
            <div class="col d-flex align-items-start gap-2">
              <i class="fa-solid fa-circle-check text-success mt-1" style="font-size:.8rem;"></i>
              <span class="small">${escapeHtml(f)}</span>
            </div>`).join('')}
        </div>
      </div>
    </div>`;
}

// Full price matrix: products (rows) × tiers (columns).
function matrixHTML(activeTi) {
  const head = PRICING.tiers.map((t, i) =>
    `<th class="text-end pricing-col ${i === activeTi ? 'active' : ''}" data-tier="${i}">${escapeHtml(t.label)}</th>`).join('');

  const body = PRICING.categories.map(c => `
    <tr class="pricing-cat-row"><td colspan="${PRICING.tiers.length + 1}" class="fw-semibold text-uppercase text-muted" style="font-size:.68rem;letter-spacing:.05em;">${escapeHtml(c.title)}</td></tr>
    ${c.products.map(p => `
      <tr>
        <td>
          <div class="fw-semibold small">${escapeHtml(p.name)}</div>
          <div class="text-muted" style="font-size:.72rem;">${escapeHtml(p.desc)}</div>
        </td>
        ${p.prices.map((v, i) => `<td class="text-end pricing-col ${i === activeTi ? 'active' : ''}" data-tier="${i}">${v == null ? '<span class="text-muted">on request</span>' : fmtUSD(v)}</td>`).join('')}
      </tr>`).join('')}`).join('');

  const totalRow = `
    <tr class="pricing-total-row">
      <td class="fw-semibold">Your configuration / month</td>
      ${PRICING.tiers.map((t, i) => `<td class="text-end fw-bold pricing-col ${i === activeTi ? 'active' : ''}" data-tier="${i}"><span class="pricing-mtotal" data-tier="${i}">${fmtUSD(totalAtTier(i))}</span></td>`).join('')}
    </tr>`;

  return `
    <h2 class="h6 mb-2">Full price list <span class="text-muted fw-normal small">— per device/service · per month · USD</span></h2>
    <div class="card shadow-sm pricing-card">
      <div class="table-responsive">
        <table class="table table-hover mb-0 align-middle pricing-matrix">
          <thead><tr><th>Device / service</th>${head}</tr></thead>
          <tbody>${body}</tbody>
          <tfoot>${totalRow}</tfoot>
        </table>
      </div>
    </div>`;
}

function notesHTML() {
  return `
    <div class="mt-4 text-muted small">
      ${PRICING.notes.map(n => `<div class="d-flex gap-2 mb-1"><i class="fa-solid fa-circle-info mt-1" style="font-size:.7rem;opacity:.6;"></i><div>${escapeHtml(n)}</div></div>`).join('')}
      ${PRICING.disclaimer ? `<div class="fw-semibold text-body mt-2">${escapeHtml(PRICING.disclaimer)}</div>` : ''}
    </div>`;
}

function accessDenied(container) {
  container.innerHTML = `<div class="alert alert-danger m-2">This page is available to superadmins only.</div>`;
}

export async function initSubscriptionPage(container) {
  const groups = window.CURRENT_USER?.groups || [];
  if (!isAdminLevel(groups)) return accessDenied(container);

  const activeTi = () => tierIndexForCount(deviceCountNow());

  const paint = () => {
    container.innerHTML = `
      <div class="pricing-page">
        <div class="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-1">
          <h1 class="h4 mb-0">Subscription plan</h1>
          <span class="badge badge-soft-gray">Price list v${escapeHtml(PRICING.version)}</span>
        </div>
        <p class="text-muted small mb-4" style="max-width:70ch;">${escapeHtml(PRICING.intro)}</p>
        ${configuratorHTML()}
        ${tierCardsHTML(activeTi())}
        ${includedHTML()}
        ${matrixHTML(activeTi())}
        ${notesHTML()}
      </div>`;
    wire();
  };

  // Update only the dynamic bits (keeps input focus) after a quantity change.
  const refresh = () => {
    const ti = activeTi();
    const sum = document.getElementById('pricing-summary');
    if (sum) sum.innerHTML = summaryHTML();
    document.querySelectorAll('#pricing-tier-cards .pricing-tier-card').forEach(el => {
      const on = Number(el.dataset.tier) === ti;
      el.classList.toggle('active', on);
      let flag = el.querySelector('.pricing-tier-flag');
      if (on && !flag) { flag = document.createElement('div'); flag.className = 'pricing-tier-flag'; flag.textContent = 'Your tier'; el.prepend(flag); }
      if (!on && flag) flag.remove();
    });
    document.querySelectorAll('.pricing-col').forEach(el =>
      el.classList.toggle('active', Number(el.dataset.tier) === ti));
    // Live per-tier "your fleet" totals (tier cards + matrix total row).
    PRICING.tiers.forEach((t, i) => {
      const money = fmtUSD(totalAtTier(i));
      const card = document.querySelector(`.pricing-tier-total[data-tier="${i}"] .v`);
      if (card) card.textContent = money;
      const cell = document.querySelector(`.pricing-mtotal[data-tier="${i}"]`);
      if (cell) cell.textContent = money;
    });
  };

  const setQty = (key, val) => {
    qty[key] = Math.max(0, Math.floor(Number(val) || 0));
    const input = document.querySelector(`.pricing-row[data-product="${key}"] .pricing-input`);
    if (input && String(qty[key]) !== input.value) input.value = qty[key];
    refresh();
  };

  function wire() {
    container.querySelectorAll('.pricing-step').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.closest('.pricing-row').dataset.product;
        setQty(key, (qty[key] || 0) + (btn.dataset.act === 'inc' ? 1 : -1));
      });
    });
    container.querySelectorAll('.pricing-input').forEach(inp => {
      inp.addEventListener('input', () => {
        const key = inp.closest('.pricing-row').dataset.product;
        qty[key] = Math.max(0, Math.floor(Number(inp.value) || 0));
        refresh();
      });
      inp.addEventListener('blur', () => setQty(inp.closest('.pricing-row').dataset.product, inp.value));
    });
    container.querySelectorAll('.pricing-check').forEach(cb => {
      cb.addEventListener('change', () => { qty[cb.dataset.product] = cb.checked ? 1 : 0; refresh(); });
    });
  }

  paint();
}
